/** Input props passed to ClipComposition at render time */
export interface ClipProps extends Record<string, unknown> {
  /** Absolute file:// URL or staticFile path to the source video */
  videoSrc: string;
  /** Source video duration in seconds (used to size the composition) */
  durationSec: number;
  /** Parsed Arabic captions (with em-tagged segments). One cue per array element. */
  cues: SerializableCue[];
  /** Speaker overlay schedule */
  speakers: SpeakerOverlay[];
  /** Background music: relative path under public/, e.g. "music/foo.mp3". Optional. */
  musicSrc?: string;
  /** 0–1 volume for the background music. Default 0.14 — audible under
   *  speech on phone speakers without fighting the dialog. Operator can
   *  override per-clip from the JobDetail screen and re-mix the audio
   *  without a full re-render. */
  musicVolume?: number;
  /** Seconds into the music track at which to start playback. Default 0
   *  (legacy clips). Set per-track via the NewJob "Pin start" button —
   *  the value is frozen onto the clip at pick time so re-renders use
   *  the same offset even if the track-level default changes later. */
  musicStartOffsetSec?: number;
  /** Brand logo path under public/, e.g. "assets/quotologies-logo.png".
   *  When omitted, falls back to the legacy Quotologies path so renders
   *  that predate the channel system (no inputProps.logoSrc baked in)
   *  still render with the original branding. */
  logoSrc?: string;
  /** Per-channel caption colors + font roles. Omitted → legacy Quotologies
   *  cream/gold + AlHurra/Lalezar. */
  brand?: RenderBrand;
  /** @font-face registrations injected into the composition. Omitted →
   *  legacy AlHurra/Faseh/Lalezar set. */
  fontFaces?: FontFaceSpec[];
  /** Per-channel caption + logo geometry. Omitted → legacy Quotologies. */
  layout?: RenderLayout;
  /** Feed cover-hold (QL_COVER_HOLD): seconds of branded cover baked in
   *  BEFORE the content — the opening frame frozen, speaker card at full
   *  opacity, punch caption, logo — so Instagram's frame-0 feed cover is
   *  never blank. Omitted/0 → no cover, composition renders exactly V1.
   *  The composition length grows by this via calculateMetadata. */
  coverHoldSec?: number;
  /** Freeze-open (QL_OPEN_FACE_HOLD): the clip's opening shows a B-roll
   *  cutaway until this many seconds in — the video track freezes the frame
   *  AT this time across [0, t) so frame 0 shows the speaker. Audio,
   *  captions, music and all other timing are untouched. Omitted/0 → the
   *  video track renders exactly as V1 (single OffthreadVideo). */
  freezeOpenUntilSec?: number;
  /** Caption shown on the cover (may carry `\n` two-line layout and `<em>`
   *  tags — rendered by the same CaptionLine as content cues). Omitted →
   *  cover without a caption. */
  coverCaptionText?: string;
  /** staticFile path to the speaker card PNG shown at FULL opacity on the
   *  cover (no fade-in). Omitted → cover without a card. */
  coverSpeakerPng?: string;
  /** Intrinsic pixel width of the cover card PNG (see SpeakerOverlay.pngWidthPx).
   *  Omitted → 1080. */
  coverSpeakerPngWidthPx?: number;
  /** Opacity (0–1) of the black darkening layer over the footage that makes
   *  captions + logo pop. Threaded from QL_VIDEO_DIM at render time. Omitted
   *  (renders that predate the knob) → DEFAULT_VIDEO_DIM, byte-identical to
   *  the founding hardcoded layer. */
  videoDim?: number;
}

/** Caption + logo geometry passed into the render (mirrors ChannelLayout
 *  in lib/channels.ts, minus the speaker arrow which the still composition
 *  reads separately). */
export interface RenderLayout {
  caption: {
    bottomPx: number;
    fontSizePx: number;
    /** Caption rendering style. Absent or "punch" = the founding behavior
     *  (big all-accent second line via `twoLine` sizes, one-line cues at
     *  fontSizePx). "uniform" = EVERY line at fontSizePx; the per-line
     *  `twoLine` sizes are ignored; color comes only from `<em>` spans
     *  (base = captionColor, spans = captionAccent); over-budget cues
     *  auto-wrap into balanced lines (src/lib/captionWrap.ts). */
    captionStyle?: "punch" | "uniform";
    /** Accent POLICY for uniform-style rendering (absent or "spans" = the
     *  founding behavior: color comes from `<em>` spans). "line-punch" =
     *  positional coloring on EVERY caption — multi-line cues render their
     *  LAST line fully in captionAccent (other lines in captionColor),
     *  one-line cues color their second half; `<em>` spans are ignored.
     *  One switch governs all cues, so it carries to future videos via
     *  channel promotion — the operator's "color the whole second line,
     *  everywhere, forever" as a design field instead of per-cue rewrites. */
    accentMode?: "spans" | "line-punch";
    /** Hard cap on rendered lines per cue in the authored/uniform wrap path
     *  (see CaptionLayout.maxLines). Absent → founding (uncapped 3-way split). */
    maxLines?: 1 | 2 | 3;
    /** Size of the LAST row of a multi-row cue relative to `fontSizePx`
     *  (rendered at round(fontSizePx × scale)). Acts ONLY when
     *  `captionStyle` is "uniform" — the punch style ignores it entirely
     *  (bigPunch has its own two sizes; the auto line-punch keeps its single
     *  size); a single-row cue is untouched. Absent = 1: the render path is
     *  exactly the previous one for every existing channel (spec 2026-09-16
     *  onboarding round 2 §2.6). */
    lastLineScale?: number;
    /** Two-line (punch) geometry. `gapPx` = distance between the lines
     *  (founding default 28; NEGATIVE allowed — lines can hug/overlap).
     *  `lineHeight` overrides the founding per-line text boxes (1.15 top /
     *  1.0 bottom) — the inter-line gap is font-box + margin driven, NOT
     *  the caption-level lineHeight field. */
    twoLine: { topPx: number; bottomPx: number; gapPx?: number; lineHeight?: number } | null;
    box: { color: string; opacity: number; radiusPx: number; padXPx: number; padYPx: number } | null;
    textShadow: string;
    sidePaddingPx: number;
    lineHeight: number;
    /** CSS letter-spacing applied to every caption line (e.g. "0.5px"; negative
     *  tightens). Omitted upstream → "0.5px" (founding Quotologies tracking). */
    letterSpacing: string;
    /** Vertical placement of the caption block. "center" = mid-frame (the
     *  founding Quotologies look, lifted slightly by bottomPx). "flex-end" =
     *  pinned bottomPx above the bottom edge. */
    justify: "center" | "flex-end";
    /** Line-breaking model (text engine v2, spec 2026-07-18). Absent or
     *  "authored" = the founding behavior: breaks come from the SRT's `\n`
     *  (uniform style still balance-wraps as in Phase 5). "auto" = BOTH
     *  styles re-break live from the real available width via measured
     *  glyphs — reducing sidePadding pushes text onto new lines, CapCut-
     *  style but deliberate: an explicit `\n` always wins. */
    wrapMode?: "authored" | "auto";
    /** Text outline (CapCut-style). Absent/null = none (founding). Drawn as
     *  a stroke layer BEHIND the fill so glyph interiors stay clean. */
    stroke?: { color: string; widthPx: number } | null;
    /** Horizontal alignment of caption lines inside the block. Absent =
     *  "center" (founding). start/end follow the text direction. */
    align?: "center" | "start" | "end";
    /** Case transform applied at render time. Absent = "none" (founding).
     *  Meaningful for Latin channels; Arabic has no case. */
    textTransform?: "none" | "uppercase";
    /** Whole-caption-block opacity 0–1. Absent = 1 (founding). */
    opacity?: number;
    /** Word-by-word karaoke highlight (phase 2). The spoken word colors
     *  (`color`, default captionAccent) — "pop" also scales it as it lands.
     *  Word timing is a length-weighted spread of the cue window
     *  (src/lib/karaoke.ts) — deterministic, language-agnostic. Absent/null
     *  = off (founding). */
    karaoke?: { mode: "highlight" | "pop"; color?: string } | null;
    /** Per-cue animation (phase 2 entrance + phase 3 exit). At least one of
     *  `in`/`out` is present. `durationMs` (entrance) and `outDurationMs`
     *  (exit) default 300. Absent/null = instant (founding). Exits are
     *  block-level only (fade/slide-down/pop) — the exit window is clamped
     *  to the last 40% of the cue so it can never overlap the entrance
     *  (which clamps to the first 60%). */
    animation?: {
      in?: "fade" | "slide-up" | "pop" | "typewriter" | "word-stagger";
      out?: "fade" | "slide-down" | "pop";
      durationMs?: number;
      outDurationMs?: number;
    } | null;
  };
  /** Footage treatment. `dim` = opacity of the black darken layer drawn over
   *  the video, UNDER captions/logo/cards, so overlays read over bright
   *  footage. Absent → 0.28 (the founding hardcoded value, byte-identical).
   *  The cover frame uses the same value so cover → content never pops in
   *  brightness.
   *
   *  `brightness` (0.5–1.6) and `contrast` (0.6–1.5) light the FOOTAGE itself
   *  (CSS filter on the video element), where `dim` only washes black over it.
   *  1 is the identity for both, and the composition MUST emit no filter when
   *  both are 1 (or absent) — that is what keeps an existing render
   *  byte-identical while the Studio baseline materializes them. */
  video?: { dim?: number; brightness?: number; contrast?: number };
  /** Timed text overlays (a brand's on-screen CTA). Absent/empty → the
   *  composition mounts nothing, byte-identical to before the layer. */
  overlays?: RenderTextOverlay[];
  /** `xPct` = horizontal centre of the logo as % of canvas width. Omitted →
   *  50 (dead-centre), reproducing the founding `left:50%` byte-identical. */
  logo: { widthPct: number; bottomPx: number; opacity: number; xPct?: number };
  /** Free x/y placement of the speaker name card (% of the 2160×3840 canvas,
   *  top-left anchor) + optional size multiplier. Omitted → founding coords
   *  (130px / 320px) at the founding 35% width, byte-identical. */
  speakerCard?: {
    xPct: number;
    yPct: number;
    scale?: number;
    /** Seconds the card stays on screen (founding 5) — consumed by
     *  buildSpeakerOverlays, not by the composition. */
    durationSec?: number;
    /** Name font cap on the generated still (founding 230). */
    nameFontPx?: number;
    /** Max words in the generated role line; 0 = no role line. */
    descriptionWords?: number;
  };
}

/** One timed text overlay burned into the render (a brand's on-screen CTA).
 *  Structural twin of `TextOverlaySpec` in lib/channels.ts — duplicated
 *  deliberately so this module keeps importing nothing (it is the shared
 *  vocabulary of the composition, the server and the browser Studio). */
export interface RenderTextOverlay {
  /** ≤ 140 chars; "\n" allowed (rendered as pre-wrap). */
  text: string;
  /** Which brand font role the overlay borrows. */
  fontRole: "caption" | "punch" | "speaker";
  /** 20–200 px on the 2160-wide canvas. */
  fontSizePx: number;
  /** #RRGGBB. */
  color: string;
  /** 0–100 % of canvas width/height; anchor follows `align`. */
  xPct: number;
  yPct: number;
  align: "start" | "center" | "end";
  /** Seconds into the CONTENT, or `{ fromEnd }` = seconds before it ends. */
  start: number | { fromEnd: number };
  /** 0.5–60 s on screen (clamped to the content end at render time). */
  durationSec: number;
}

/** Caption + speaker color/font set passed into the render. */
export interface RenderBrand {
  /** Caption body + speaker-card description color (legacy cream). */
  captionColor: string;
  /** Em words, two-line punch line, speaker-card name (legacy gold). */
  captionAccent: string;
  /** Caption body font family (legacy "AlHurra"). */
  captionFont: string;
  /** Two-line punch + digit-run font family (legacy "Lalezar"). */
  punchFont: string;
  /** Speaker-card font family (legacy "Faseh"). */
  speakerFont: string;
  /** Caption text direction. Omitted → "rtl" (legacy Quotologies / Arabic
   *  channels). Set from the channel's resolved language so an LTR (e.g.
   *  French) channel renders left-to-right. */
  direction?: "rtl" | "ltr";
  /** Approx glyph advance (em) for the one-line-fit heuristic in CaptionLine.
   *  Omitted → 0.6 (Arabic). Latin scripts pack narrower (~0.5), so more
   *  characters fit before a cue wraps to two lines. */
  glyphRatio?: number;
}

/** A single @font-face the composition registers via a <style> tag. */
export interface FontFaceSpec {
  family: string;
  weight: number;
  /** Path under public/ for staticFile(), e.g. "fonts/AlHurraTxtBold Bold.ttf". */
  src: string;
}

export interface SerializableCue {
  startMs: number;
  endMs: number;
  /** Plain Arabic text. May contain a single `\n` for the two-line layout. */
  text: string;
}

/** A speaker card rendered LIVE from text instead of a pre-generated PNG —
 *  the Studio/onboarding preview path, where there is no still on disk yet.
 *  The render pipeline never sets it (it always ships a real PNG), so a
 *  production render is byte-identical to before this existed. */
export interface SpeakerCardLive {
  name: string;
  description: string;
  direction: "rtl" | "ltr";
  nameColor: string;
  descColor: string;
  /** Family for the description (and the name, when `nameFont` is absent). */
  font: string;
  /** Family for the NAME only, when the brand splits the two roles. */
  nameFont?: string;
  /** @font-face registrations the preview needs to draw those families. */
  fontFaces: FontFaceSpec[];
}

export interface SpeakerOverlay {
  slug: string;
  /** Absolute file:// URL or staticFile path to the speaker PNG */
  pngSrc: string;
  /** Time at which the card appears, in seconds from clip start */
  startSec: number;
  /** How long the card stays visible */
  durationSec: number;
  /** Intrinsic pixel width of the PNG. The card box scales with this so a
   *  still rendered on the widened canvas shows glyphs at the SAME on-screen
   *  size as a founding 1080px card. Omitted → 1080 (every cached pre-widening
   *  card renders byte-identical). */
  pngWidthPx?: number;
  /** Preview-only: draw the card from text instead of `pngSrc`. Absent on
   *  every pipeline render. */
  live?: SpeakerCardLive;
}

export const QUOT = {
  /** Caption cream */
  CREAM: "#F2EBD8",
  /** Caption + speaker name gold */
  GOLD: "#FFBF17",
} as const;

export const FPS = 30;
export const CANVAS_WIDTH = 2160;
export const CANVAS_HEIGHT = 3840;

/** Founding speaker-card still canvas width — every card generated before
 *  the 2026-08-13 widening (and every hand-made Canva export) is this wide.
 *  Placement math treats it as the reference: a PNG of this width renders
 *  at the founding 35% card box. */
export const SPEAKER_STILL_FOUNDING_WIDTH = 1080;
/** Current still canvas. Widened 1080 → 2160 so a long name at the uniform
 *  font size extends toward frame center on one line instead of shrinking.
 *  2160 keeps the numbers exact: placed width 35% × 2160/1080 = 70%, and
 *  the still→canvas glyph scale stays the founding 0.7 (2160×0.70/2160).
 *  The canvas is transparent — only the glyph run is visible, so the wide
 *  box costs nothing on screen. */
export const SPEAKER_STILL_WIDTH = 2160;
export const SPEAKER_STILL_HEIGHT = 1350;

// ---------------------------------------------------------------------------
// Legacy (founding Quotologies) baselines — the values every composition
// element falls back to when `brand` / `layout` are absent from ClipProps.
// Single-sourced here so the components (CaptionLine, LogoMark, SpeakerCard)
// and the Studio edit layer (src/lib/clipEditState.ts) can never drift: the
// Studio inspector shows these exact numbers for a legacy clip, and
// materializing them into an explicit layout is render-identical by
// construction.
// ---------------------------------------------------------------------------

/** Founding caption drop-shadow. */
export const DEFAULT_TEXT_SHADOW =
  "0 4px 12px rgba(0,0,0,0.85), 0 2px 4px rgba(0,0,0,0.85)";

/** Founding footage darken-layer opacity (the value hardcoded in
 *  ClipComposition/CoverFrame before layout.video existed / the QL_VIDEO_DIM
 *  knob, 2026-07-18). Single-sourced so the compositions, the Studio baseline,
 *  parseLayout and the inputProps.videoDim fallback can never drift. */
export const DEFAULT_VIDEO_DIM = 0.28;

/** Brand set used when ClipProps.brand is absent. `direction` and
 *  `glyphRatio` are the components' implicit `??` fallbacks made explicit. */
export const LEGACY_RENDER_BRAND: RenderBrand = {
  captionColor: QUOT.CREAM,
  captionAccent: QUOT.GOLD,
  captionFont: "AlHurra",
  punchFont: "Lalezar",
  speakerFont: "Faseh",
  direction: "rtl",
  glyphRatio: 0.6,
};

/** Caption geometry used when ClipProps.layout is absent (CaptionLine's
 *  founding constants). */
export const LEGACY_CAPTION_LAYOUT: RenderLayout["caption"] = {
  bottomPx: 240,
  fontSizePx: 115,
  captionStyle: "punch",
  twoLine: { topPx: 110, bottomPx: 220 },
  box: null,
  textShadow: DEFAULT_TEXT_SHADOW,
  sidePaddingPx: 200,
  lineHeight: 1.2,
  letterSpacing: "0.5px",
  justify: "center",
};

/** Logo geometry used when ClipProps.layout is absent (LogoMark's founding
 *  prop defaults). */
export const LEGACY_LOGO_LAYOUT: Required<RenderLayout["logo"]> = {
  widthPct: 36,
  bottomPx: 620,
  opacity: 1,
  xPct: 50,
};

/** Founding speaker-card placement in canvas px (matches CapCut Scale 35%).
 *  SpeakerCard re-exports these as CARD_LEFT_PX / CARD_TOP_PX. */
export const LEGACY_SPEAKER_CARD_LEFT_PX = 130;
export const LEGACY_SPEAKER_CARD_TOP_PX = 320;
/** Founding card width as % of canvas width. */
export const LEGACY_SPEAKER_CARD_WIDTH_PCT = 35;
